import { useEffect, useRef, useState } from 'react';
import { NavLink, useNavigate } from 'react-router-dom';
import { Menu, MenuButton, MenuItem, MenuItems } from '@headlessui/react';
import { BrandLogo } from './BrandLogo';
import { SearchIcon } from '@/components/ui/Icons';
import { mockClinicianName } from '@/data/mock';
import { useTheme } from '@/lib/theme';

const tabClass = ({ isActive }: { isActive: boolean }) =>
  [
    'text-[13px] font-medium px-2.5 py-1.5 rounded-md cursor-pointer select-none transition-colors',
    isActive
      ? 'text-primary bg-soft font-semibold'
      : 'text-tertiary hover:text-primary hover:bg-soft',
  ].join(' ');

export function AppBar() {
  const navigate = useNavigate();
  const { theme, toggle } = useTheme();
  const [searchOpen, setSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const searchInputRef = useRef<HTMLInputElement | null>(null);
  const searchContainerRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (searchOpen) {
      searchInputRef.current?.focus();
    }
  }, [searchOpen]);

  useEffect(() => {
    if (!searchOpen) return;
    const onMouseDown = (e: MouseEvent) => {
      if (
        searchContainerRef.current &&
        !searchContainerRef.current.contains(e.target as Node)
      ) {
        setSearchOpen(false);
      }
    };
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setSearchOpen(false);
    };
    document.addEventListener('mousedown', onMouseDown);
    document.addEventListener('keydown', onKey);
    return () => {
      document.removeEventListener('mousedown', onMouseDown);
      document.removeEventListener('keydown', onKey);
    };
  }, [searchOpen]);

  return (
    <header className="sticky top-0 z-50 bg-frost backdrop-blur-xl backdrop-saturate-150 border-b border-line h-[52px] flex items-center gap-3.5 px-8 max-[1024px]:px-6 max-[760px]:px-4 max-[760px]:gap-2.5">
      <NavLink to="/overview" className="inline-flex items-center shrink-0 text-primary leading-none">
        <BrandLogo width={116} />
      </NavLink>

      <nav className="flex gap-0.5 ml-1 max-[760px]:ml-auto">
        <NavLink to="/overview" className={tabClass}>
          Panel
        </NavLink>
        <NavLink to="/today" className={tabClass}>
          Hoy
        </NavLink>
        <NavLink
          to="/patients"
          className={({ isActive }) =>
            tabClass({
              isActive: isActive || window.location.pathname.startsWith('/patients'),
            })
          }
        >
          Pacientes
        </NavLink>
        <NavLink to="/signals" className={tabClass}>
          Campañas
        </NavLink>
      </nav>

      <div
        ref={searchContainerRef}
        className={`relative flex items-center transition-[width] duration-200 ease-out ${
          searchOpen ? 'w-[280px] max-[760px]:w-[200px]' : 'w-8'
        }`}
      >
        <button
          type="button"
          aria-label="Buscar paciente"
          title="Buscar paciente"
          onClick={() => setSearchOpen((v) => !v)}
          className={`grid place-items-center w-8 h-8 text-tertiary bg-transparent border-0 rounded-md cursor-pointer transition-colors hover:text-primary hover:bg-soft ${
            searchOpen ? 'absolute left-1.5 top-1/2 -translate-y-1/2 z-10 hover:bg-transparent' : ''
          }`}
        >
          <SearchIcon />
        </button>
        {searchOpen && (
          <input
            ref={searchInputRef}
            type="search"
            placeholder="Buscar paciente…"
            autoComplete="off"
            value={searchValue}
            onChange={(e) => setSearchValue(e.target.value)}
            className="w-full h-8 pl-8 pr-3 bg-glass backdrop-blur-md border border-line-glass rounded-md outline-0 text-[13px] placeholder:text-faint shadow-recessed focus:bg-surface focus:border-line-default"
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                const query = searchValue.trim();
                navigate(`/patients${query ? `?q=${encodeURIComponent(query)}` : ''}`);
                setSearchOpen(false);
              }
            }}
          />
        )}
      </div>

      <span className="flex-1" />

      <Menu as="div" className="relative shrink-0">
        <MenuButton className="flex items-center gap-1.5 text-[13px] text-tertiary bg-transparent border-0 rounded-md px-2 py-1.5 h-8 cursor-pointer transition-colors hover:text-primary hover:bg-soft data-[open]:bg-soft data-[open]:text-primary">
          <span className="max-[760px]:hidden">{mockClinicianName}</span>
          <ChevronDownIcon />
        </MenuButton>
        <MenuItems
          anchor={{ to: 'bottom end', gap: 6 }}
          transition
          className="z-[60] w-[224px] origin-top-right bg-surface border border-line rounded-md shadow-elevated p-1 focus:outline-none transition duration-150 ease-out data-[closed]:opacity-0 data-[closed]:scale-95 data-[enter]:data-[closed]:translate-y-1"
        >
          <div className="px-2.5 py-2 border-b border-line mb-1">
            <div className="text-[10.5px] font-medium tracking-[0.06em] uppercase text-muted">
              Sesión activa
            </div>
            <div className="text-[13px] text-primary mt-0.5 truncate">
              {mockClinicianName}
            </div>
          </div>
          <MenuItem>
            <button
              type="button"
              onClick={(e) => toggle(e)}
              className="w-full flex items-center gap-2 text-[13px] text-secondary bg-transparent border-0 px-2.5 py-2 rounded-sm cursor-pointer transition-colors data-[focus]:bg-soft data-[focus]:text-primary"
            >
              {theme === 'dark' ? <SunIcon /> : <MoonIcon />}
              <span>{theme === 'dark' ? 'Modo claro' : 'Modo oscuro'}</span>
            </button>
          </MenuItem>
          <MenuItem>
            <button
              type="button"
              onClick={() => navigate('/login')}
              className="w-full flex items-center gap-2 text-[13px] text-secondary bg-transparent border-0 px-2.5 py-2 rounded-sm cursor-pointer transition-colors data-[focus]:bg-soft data-[focus]:text-primary"
            >
              <LogoutIcon />
              <span>Salir</span>
            </button>
          </MenuItem>
        </MenuItems>
      </Menu>
    </header>
  );
}

function ChevronDownIcon() {
  return (
    <svg width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="m6 9 6 6 6-6" />
    </svg>
  );
}

function SunIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <circle cx="12" cy="12" r="4" />
      <path d="M12 2v2M12 20v2M4.93 4.93l1.41 1.41M17.66 17.66l1.41 1.41M2 12h2M20 12h2M4.93 19.07l1.41-1.41M17.66 6.34l1.41-1.41" />
    </svg>
  );
}

function MoonIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M21 12.79A9 9 0 1 1 11.21 3 7 7 0 0 0 21 12.79z" />
    </svg>
  );
}

function LogoutIcon() {
  return (
    <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4M16 17l5-5-5-5M21 12H9" />
    </svg>
  );
}
